'use client'

import type { AttributionData } from '@/lib/ghl/types'

/** Keep in sync with the inline capture script in src/app/layout.tsx. */
export const ATTRIBUTION_STORAGE_KEY = 'bl_attribution'

/**
 * Reads the attribution captured by the layout's inline script (first landing
 * page, current/conversion page, referrer, UTM + click IDs), optionally
 * overlaid with page-specific context (page family, content topic, CTA text)
 * that only the calling page knows.
 */
export function getStoredAttribution(
  overrides: Partial<AttributionData> = {}
): AttributionData {
  if (typeof window === 'undefined') return overrides

  let stored: AttributionData = {}
  try {
    stored = JSON.parse(window.localStorage.getItem(ATTRIBUTION_STORAGE_KEY) ?? '{}')
  } catch {
    stored = {}
  }

  return {
    ...stored,
    conversionPageUrl: window.location.href,
    ...overrides,
  }
}

/**
 * Appends page/CTA/UTM attribution as query params to an outbound URL (a GHL
 * booking widget, or app.builderlync.com signup). Client-side only. Whether the
 * destination widget actually consumes these params needs live verification
 * (flagged as an open question in the plan) — worst case they're inert.
 */
export function buildAttributedUrl(base: string, ctx: { page: string; cta: string }): string {
  if (typeof window === 'undefined') return base
  const attribution = getStoredAttribution()
  const params = new URLSearchParams()
  if (attribution.utmSource) params.set('utm_source', attribution.utmSource)
  if (attribution.utmMedium) params.set('utm_medium', attribution.utmMedium)
  if (attribution.utmCampaign) params.set('utm_campaign', attribution.utmCampaign)
  params.set('bl_page', ctx.page)
  params.set('bl_cta', ctx.cta)
  const separator = base.includes('?') ? '&' : '?'
  return `${base}${separator}${params.toString()}`
}
